/**
 * Marcador de build. Cambiarlo fuerza hashes nuevos en los ficheros de
 * `dist/assets`, que es la ÚNICA salida cuando un deploy de Pages deja un
 * chunk sin subir: ese asset se sirve como el index.html (200 text/html) y,
 * con el `immutable` de `_headers`, el navegador cachea la respuesta
 * equivocada durante un año y la app queda tostada con
 * "Expected a JavaScript-or-Wasm module script".
 *
 * Pasó en el primer despliegue de jobs-app (2026-07-25): el CSS subió y el
 * JS no. Un redeploy no basta, porque un build determinista reproduce el
 * MISMO hash y los navegadores afectados siguen sirviendo su copia
 * envenenada. Bumpea esto y redespliega.
 */
export const CLIENT_BUILD = "20260725a";

export const API_URL = import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8787";

/**
 * Cliente de la API del portal de reclutadores.
 *
 * A diferencia de notes-app y posts-app (que autentican con un PAT Bearer),
 * aquí la sesión va en una **cookie httpOnly propia del portal**
 * (`__Host-recruiter`), así que todas las llamadas van con
 * `credentials: "include"` y las escrituras necesitan el doble envío CSRF.
 */

let csrfToken: string | null = null;

/**
 * Obtiene el token CSRF del CUERPO de `GET /api/auth/csrf`, no de
 * `document.cookie`.
 *
 * La cookie es `__Host-csrf`, y el prefijo `__Host-` la hace host-only de
 * `api.elcybercurioso.com`. Esta SPA vive en `jobs.elcybercurioso.com`, así
 * que `document.cookie` NUNCA puede leerla: el navegador la envía sola con
 * `credentials: "include"`, pero no la expone a este origen. Por eso el
 * endpoint devuelve el token también en el cuerpo — es el mismo mecanismo
 * que usa apps/web, que está en otro host que la API.
 */
export async function ensureCsrf(): Promise<string | null> {
  if (csrfToken) return csrfToken;
  try {
    const r = await fetch(`${API_URL}/api/auth/csrf`, { credentials: "include" });
    const j = (await r.json()) as { csrf?: string };
    csrfToken = j.csrf ?? null;
  } catch {
    csrfToken = null;
  }
  return csrfToken;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public payload: unknown = null,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export async function api<T>(
  path: string,
  init: RequestInit & { raw?: boolean } = {},
  // Interno: evita bucles en el reintento por CSRF caducado.
  retriedCsrf = false,
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const t = await ensureCsrf();
    if (t) headers.set("x-csrf-token", t);
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    method,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    let code = `http_${res.status}`;
    let payload: unknown = null;
    try {
      payload = await res.json();
      const p = payload as { error?: string };
      if (p?.error) code = p.error;
    } catch {
      /* respuesta sin JSON */
    }

    // El servidor rota el token CSRF al iniciar sesión (rotateCsrf), así que
    // el que tenemos cacheado queda obsoleto justo después del login. En vez
    // de hacer fallar la primera escritura de la sesión, se descarta la
    // caché, se pide uno nuevo y se reintenta UNA vez.
    // Un body de FormData no se puede reenviar si ya se consumió su stream;
    // en la práctica no hay escrituras multipart en este portal.
    if (code === "csrf_failed" && !retriedCsrf && !(init.body instanceof FormData)) {
      csrfToken = null;
      return api<T>(path, init, true);
    }

    throw new ApiError(res.status, code, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Descarga binaria (el CV). Devuelve el blob para forzar el guardado. */
export async function apiBlob(path: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_URL}${path}`, { credentials: "include" });
  if (!res.ok) throw new ApiError(res.status, `http_${res.status}`);
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = cd.match(/filename="([^"]+)"/);
  return { blob: await res.blob(), filename: m ? m[1] : "cv.pdf" };
}
